import React from 'react';
import {
  SessionRecord,
  formatSessionAmount,
  formatSessionDate,
  formatSessionTime,
  getSessionTypeName,
} from '@theraply/api-client';

interface ReceiptPreviewProps {
  session: SessionRecord;
  onClose: () => void;
  onDownload: (sessionId: number) => void;
  onPrint: () => void;
}

export const ReceiptPreview: React.FC<ReceiptPreviewProps> = ({
  session,
  onClose,
  onDownload,
  onPrint,
}) => {
  const sessionTypeName = getSessionTypeName(session.sessionType);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-start p-6 border-b border-gray-200">
          <h2 className="text-2xl font-bold text-gray-900">Receipt Preview</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        {/* Receipt Content */}
        <div id="receipt-content" className="p-8 space-y-6">
          {/* Platform Header */}
          <div className="text-center pb-6 border-b-2 border-gray-300">
            <h1 className="text-3xl font-bold text-blue-600">Theraply</h1>
            <p className="text-lg font-semibold text-gray-700 mt-2">
              THERAPY SESSION RECEIPT
            </p>
          </div>

          {/* Receipt Info */}
          <div className="grid grid-cols-2 gap-4 pb-6 border-b border-gray-200">
            <div>
              <p className="text-sm font-medium text-gray-500">Receipt ID</p>
              <p className="text-base font-semibold text-gray-900">
                RCP-{session.id}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Receipt Date</p>
              <p className="text-base font-semibold text-gray-900">
                {formatSessionDate(session.updatedAt)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Session Date</p>
              <p className="text-base font-semibold text-gray-900">
                {formatSessionDate(session.sessionDate)}
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500">Payment Method</p>
              <p className="text-base font-semibold text-gray-900">
                Credit Card (Stripe)
              </p>
            </div>
          </div>

          {/* Participants */}
          <div className="pb-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Session Participants
            </h3>
            <div className="grid grid-cols-2 gap-6">
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm font-medium text-gray-500 mb-1">Client</p>
                <p className="text-base font-semibold text-gray-900">
                  {session.consumerName || 'Unknown'}
                </p>
              </div>
              <div className="bg-gray-50 p-4 rounded">
                <p className="text-sm font-medium text-gray-500 mb-1">
                  Therapist
                </p>
                <p className="text-base font-semibold text-gray-900">
                  {session.therapistName || 'Unknown'}
                </p>
              </div>
            </div>
          </div>

          {/* Session Details */}
          <div className="pb-6 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Session Details
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">Session Type:</span>
                <span className="font-medium text-gray-900">{sessionTypeName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Time:</span>
                <span className="font-medium text-gray-900">
                  {formatSessionTime(session.startTime)} -{' '}
                  {formatSessionTime(session.endTime)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Duration:</span>
                <span className="font-medium text-gray-900">
                  {session.durationMinutes} minutes
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className="font-medium text-green-600">
                  {session.status}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Breakdown */}
          <div className="pb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Payment Breakdown
            </h3>
            <div className="space-y-3">
              <div className="flex justify-between text-base">
                <span className="text-gray-600">Session Fee</span>
                <span className="font-medium text-gray-900">
                  {formatSessionAmount(session.amountPaid)}
                </span>
              </div>
              <div className="flex justify-between text-base">
                <span className="text-gray-600">Platform Fee</span>
                <span className="font-medium text-gray-900">
                  -{formatSessionAmount(session.platformFee)}
                </span>
              </div>
              <div className="flex justify-between text-base">
                <span className="text-gray-600">Therapist Earnings</span>
                <span className="font-medium text-gray-900">
                  {formatSessionAmount(session.therapistEarnings)}
                </span>
              </div>
              <div className="pt-3 border-t-2 border-gray-300 flex justify-between text-xl">
                <span className="font-bold text-gray-900">Total Paid</span>
                <span className="font-bold text-gray-900">
                  {formatSessionAmount(session.amountPaid)}
                </span>
              </div>
            </div>
            {session.stripePaymentId && (
              <p className="text-xs text-gray-500 mt-4">
                Payment ID: {session.stripePaymentId}
              </p>
            )}
          </div>

          {/* Footer */}
          <div className="text-center text-sm text-gray-500 pt-6 border-t border-gray-200">
            <p>Theraply</p>
            <p className="mt-1">
              This is an automatically generated receipt. No signature required.
            </p>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <button
            onClick={() => onDownload(session.id)}
            className="flex-1 px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-medium"
          >
            Download PDF
          </button>
          <button
            onClick={onPrint}
            className="flex-1 px-6 py-3 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors font-medium"
          >
            Print Receipt
          </button>
          <button
            onClick={onClose}
            className="flex-1 px-6 py-3 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors font-medium"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};
